import { formatMoney } from "../../../api/money";
import PartPicker from "./PartPicker";
import type { CataloguePart, Issue, ReadLine } from "./readerApi";

/** What the office decided an unmatched line is: a catalogue part, or a
 * charge that is not one. Nothing is decided for them. */
export type Resolution = { kind: "part"; part: CataloguePart } | { kind: "sundry" };

interface Props {
  lines: ReadLine[];
  catalogue: CataloguePart[];
  issues: Issue[];
  resolutions: Record<number, Resolution>;
  onResolve: (seq: number, resolution: Resolution | null) => void;
}

const KIND_LABEL: Record<ReadLine["kind"], string> = {
  part: "part",
  labour: "Labour",
  travel: "Travel",
  unmatched: "—",
};

/** What the Code column shows: the catalogue code where there is one, the
 * kind of charge where the line is not a part, and what the office decided
 * where it was unmatched. */
function codeCell(line: ReadLine, resolution: Resolution | undefined): string {
  if (line.part !== null) {
    return line.part.code;
  }
  if (line.kind !== "unmatched") {
    return KIND_LABEL[line.kind];
  }
  if (resolution === undefined) {
    // The code the quote printed, so the office can see what the list is missing.
    return line.item_code || "—";
  }
  return resolution.kind === "part" ? resolution.part.code : "sundry";
}

function rowState(line: ReadLine, resolution: Resolution | undefined): string {
  if (line.kind === "unmatched") {
    return resolution ? "resolved" : "blocking";
  }
  return line.price_matches ? "" : "warning";
}

/** The quotation's lines as the order will carry them: the catalogue's code
 * and wording where a line matched, the supplier's own where it did not, and
 * under each line what the check found about it. An unmatched line blocks
 * until the office says what it is. */
export default function LinesTable({ lines, catalogue, issues, resolutions, onResolve }: Props) {
  return (
    <div className="table-card">
      <table className="data-table qr-lines">
        <thead>
          <tr>
            <th className="cm-seq">#</th>
            <th>Code</th>
            <th>Line</th>
            <th className="num">Qty</th>
            <th className="num">Price list</th>
            <th className="num">Quoted</th>
          </tr>
        </thead>
        <tbody>
          {lines.map((line, index) => {
            const resolution = resolutions[line.seq];
            const unmatched = line.kind === "unmatched";
            const state = rowState(line, resolution);
            const notes = issues.filter((issue) => issue.line_seq === line.seq && issue.code !== "UNMATCHED");
            return (
              <tr key={`${line.seq}-${index}`} className={`qr-row ${state}`.trim()} data-testid={`reader-line-${line.seq}`}>
                <td className="cm-seq muted">{line.seq}</td>
                <td className="mono">{codeCell(line, resolution)}</td>
                <td>
                  <div>
                    {resolution?.kind === "part"
                      ? resolution.part.description
                      : unmatched
                        ? line.source_description
                        : line.description}
                    {!unmatched && line.size ? ` — ${line.size}` : ""}
                  </div>
                  <span className="sub">As quoted: {line.source_description}</span>
                  {notes.map((issue) => (
                    <span key={`${issue.code}-${issue.message}`} className={`qr-line-note ${issue.severity}`}>
                      {issue.message}
                    </span>
                  ))}
                  {unmatched && resolution === undefined ? (
                    <PartPicker
                      quoted={line.source_description}
                      catalogue={catalogue}
                      onPart={(part) => onResolve(line.seq, { kind: "part", part })}
                      onSundry={() => onResolve(line.seq, { kind: "sundry" })}
                    />
                  ) : null}
                  {unmatched && resolution !== undefined ? (
                    <span className="qr-resolved">
                      {resolution.kind === "sundry"
                        ? "Kept as a sundry, as quoted"
                        : `Resolved as ${resolution.part.code} ${resolution.part.description}`}{" "}
                      ·{" "}
                      <button type="button" className="go-link" onClick={() => onResolve(line.seq, null)}>
                        Change
                      </button>
                    </span>
                  ) : null}
                </td>
                <td className="num">{line.qty}</td>
                <td className="num">
                  {/* The price list's own figure. It showed the order's cost, which
                      read as "Price list R2 137,50" beside a note that the list says
                      R2 325,00, and as the share for a bundle's part. */}
                  {formatMoney(
                    resolution?.kind === "part"
                      ? resolution.part.unit_cost
                      : line.part
                        ? line.part.unit_cost
                        : line.unit_cost,
                  )}
                </td>
                <td className={line.price_matches || unmatched ? "num" : "num qr-quoted-off"}>
                  {line.quoted_unit_cost === null ? (
                    <span title="Priced by the supplier as part of a bundle">bundle</span>
                  ) : line.bundled ? (
                    <span title="Its share of the price the supplier quoted for the whole bundle">
                      {`${formatMoney(line.quoted_unit_cost)} · bundle share`}
                    </span>
                  ) : (
                    formatMoney(line.quoted_unit_cost)
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
